(() => {
  if (window.KohlPanettoneCartGuard) return;

  const PRODUCT_ID = '16317752738179';
  const VARIANT_ID = '62625055179139';
  const ALLOWED_GIFT_PRODUCT_IDS = new Set([
    '15621892899203', // Bergapfelsaft Calidum (gift eligibility managed by Kite)
    '15901533929859'  // Marmorwürfel
  ]);
  const ALLOWED_GIFT_VARIANT_IDS = new Set([
    '57313032634755', // Calidum 750 ml
    '57805816136067'  // Marmorwürfel
  ]);
  const originalFetch = window.fetch.bind(window);
  const root = () => window.Shopify?.routes?.root || '/';

  const translations = {
    de: {
      title: 'Separat von anderen Produkten bestellen',
      message: 'Die KOHL x Raro Panettone Box muss separat von anderen Produkten bestellt werden. Mehrere Panettone Boxen sind möglich. Erlaubt sind die Gratisgeschenke: 1 Flasche Calidum ab 139 € und Marmorwürfel ab 200 € Bestellwert. Für weitere Produkte gib bitte eine separate Bestellung auf.',
      close: 'Verstanden'
    },
    it: {
      title: 'Da ordinare separatamente dagli altri prodotti',
      message: 'La KOHL x Raro Panettone Box deve essere ordinata separatamente dagli altri prodotti. È possibile ordinare più Panettone Box. Sono ammessi gli omaggi: 1 bottiglia di Calidum da 139 € e cubetti in marmo da 200 € di valore dell’ordine. Per altri prodotti, effettua un ordine separato.',
      close: 'Ho capito'
    },
    en: {
      title: 'Order separately from other products',
      message: 'The KOHL x Raro Panettone Box must be ordered separately from other products. You can order multiple Panettone Boxes. The free gifts are allowed: 1 bottle of Calidum on orders of €139 or more and marble cubes on orders of €200 or more. For other products, please place a separate order.',
      close: 'Got it'
    }
  };

  const language = (document.documentElement.lang || 'de').toLowerCase().split('-')[0];
  const copy = translations[language] || translations.en;
  let lastFocusedElement;
  let cartIsInvalid = false;

  const isPanettone = item =>
    String(item?.product_id || '') === PRODUCT_ID || String(item?.variant_id || item?.id || '') === VARIANT_ID;

  const isAllowedGift = item =>
    // Kite sets this property on gift additions before the discount is applied.
    // A normally purchased bottle/cube must not bypass the Panettone restriction.
    String(item?.properties?._free_product) === 'true' && (
      ALLOWED_GIFT_PRODUCT_IDS.has(String(item?.product_id || '')) ||
      ALLOWED_GIFT_VARIANT_IDS.has(String(item?.variant_id || item?.id || ''))
    );

  const createModal = () => {
    if (document.querySelector('[data-panettone-guard-modal]')) return;

    const modal = document.createElement('div');
    modal.className = 'panettone-guard';
    modal.hidden = true;
    modal.dataset.panettoneGuardModal = '';
    modal.innerHTML = `
      <div class="panettone-guard__backdrop" data-panettone-guard-close></div>
      <div class="panettone-guard__dialog" role="dialog" aria-modal="true" aria-labelledby="PanettoneGuardTitle" aria-describedby="PanettoneGuardMessage">
        <button class="panettone-guard__x" type="button" aria-label="${copy.close}" data-panettone-guard-close>&times;</button>
        <h2 class="panettone-guard__title h3" id="PanettoneGuardTitle">${copy.title}</h2>
        <p class="panettone-guard__message" id="PanettoneGuardMessage">${copy.message}</p>
        <button class="button panettone-guard__button" type="button" data-panettone-guard-close>${copy.close}</button>
      </div>`;
    document.body.appendChild(modal);

    modal.addEventListener('click', event => {
      if (event.target.closest('[data-panettone-guard-close]')) closeModal();
    });
  };

  const showModal = () => {
    createModal();
    const modal = document.querySelector('[data-panettone-guard-modal]');
    lastFocusedElement = document.activeElement;
    modal.hidden = false;
    document.body.classList.add('panettone-guard-open');
    modal.querySelector('.panettone-guard__button')?.focus();
  };

  const closeModal = () => {
    const modal = document.querySelector('[data-panettone-guard-modal]');
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    document.body.classList.remove('panettone-guard-open');
    lastFocusedElement?.focus?.();
  };

  const cartUrl = () => `${root()}cart.js`;
  const getCart = async () => {
    const response = await originalFetch(cartUrl(), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    });
    if (!response.ok) throw new Error('Unable to load cart');
    return response.json();
  };

  const endpoint = input => {
    try {
      const rawUrl = input instanceof Request ? input.url : String(input);
      const path = new URL(rawUrl, window.location.origin).pathname;
      const match = path.match(/\/cart\/(add|change|update)(?:\.js)?\/?$/);
      return match?.[1] || '';
    } catch (_error) {
      return '';
    }
  };

  const readBody = async (input, init) => {
    if (init?.body instanceof FormData || init?.body instanceof URLSearchParams) return init.body;
    if (typeof init?.body === 'string') {
      try { return JSON.parse(init.body); } catch (_error) { return new URLSearchParams(init.body); }
    }
    if (init?.body && typeof init.body === 'object') return init.body;

    if (input instanceof Request && input.method !== 'GET' && input.method !== 'HEAD') {
      const clone = input.clone();
      const contentType = clone.headers.get('content-type') || '';
      if (contentType.includes('application/json')) return clone.json();
      try { return await clone.formData(); } catch (_error) { return {}; }
    }
    return {};
  };

  const positiveQuantity = value => Math.max(0, Number.parseInt(value, 10) || 0);

  const addItemsFromBody = body => {
    if (body instanceof FormData || body instanceof URLSearchParams) {
      const directId = body.get('id');
      if (directId) return [{
        id: directId,
        quantity: positiveQuantity(body.get('quantity') || 1),
        properties: { _free_product: body.get('properties[_free_product]') }
      }];

      const indexed = new Map();
      for (const [key, value] of body.entries()) {
        const match = key.match(/^items\[(\d+)\]\[(id|quantity|properties)\](?:\[(_free_product)\])?$/);
        if (!match) continue;
        const item = indexed.get(match[1]) || {};
        if (match[2] === 'properties') {
          if (match[3]) item.properties = { _free_product: value };
        } else {
          item[match[2]] = value;
        }
        indexed.set(match[1], item);
      }
      return Array.from(indexed.values()).map(item => ({
        id: item.id,
        quantity: positiveQuantity(item.quantity || 1),
        properties: item.properties
      }));
    }

    if (Array.isArray(body?.items)) {
      return body.items.map(item => ({ id: item.id, quantity: positiveQuantity(item.quantity || 1), properties: item.properties }));
    }
    if (body?.id) return [{ id: body.id, quantity: positiveQuantity(body.quantity || 1), properties: body.properties }];
    return [];
  };

  const changesFromBody = body => {
    if (body instanceof FormData || body instanceof URLSearchParams) {
      const updates = body.getAll('updates[]');
      if (updates.length) return { updates: updates.map(positiveQuantity) };
      return {
        line: body.get('line'),
        id: body.get('id'),
        quantity: body.has('quantity') ? positiveQuantity(body.get('quantity')) : null
      };
    }
    return body || {};
  };

  const resultingItems = (cart, operation, body) => {
    const items = (cart.items || []).map(item => ({ ...item, quantity: positiveQuantity(item.quantity) }));
    const changes = changesFromBody(body);

    if (operation === 'change') {
      const index = changes.line
        ? Number.parseInt(changes.line, 10) - 1
        : items.findIndex(item => String(item.key) === String(changes.id) || String(item.variant_id) === String(changes.id));
      if (index >= 0 && changes.quantity !== null && changes.quantity !== undefined) {
        items[index].quantity = positiveQuantity(changes.quantity);
      }
    }

    if (operation === 'update') {
      if (Array.isArray(changes.updates)) {
        changes.updates.forEach((quantity, index) => {
          if (items[index]) items[index].quantity = positiveQuantity(quantity);
        });
      } else if (changes.updates && typeof changes.updates === 'object') {
        Object.entries(changes.updates).forEach(([id, quantity]) => {
          const item = items.find(entry => String(entry.key) === String(id) || String(entry.variant_id) === String(id));
          if (item) item.quantity = positiveQuantity(quantity);
        });
      }
    }

    return items.filter(item => item.quantity > 0);
  };

  const violatesRule = items => {
    const panettoneQuantity = items.filter(isPanettone).reduce((sum, item) => sum + positiveQuantity(item.quantity), 0);
    const otherQuantity = items
      .filter(item => !isPanettone(item) && !isAllowedGift(item))
      .reduce((sum, item) => sum + positiveQuantity(item.quantity), 0);
    // Each promotion grants one gift, not extra paid units of that product.
    const giftQuantities = new Map();
    items.filter(isAllowedGift).forEach(item => {
      const id = String(item.variant_id || item.id || item.product_id);
      giftQuantities.set(id, (giftQuantities.get(id) || 0) + positiveQuantity(item.quantity));
    });
    const excessGifts = [...giftQuantities.values()].some(quantity => quantity > 1);
    return panettoneQuantity > 0 && (otherQuantity > 0 || excessGifts);
  };

  const setCartState = cart => {
    cartIsInvalid = violatesRule(cart?.items || []);
    document.documentElement.classList.toggle('panettone-cart-invalid', cartIsInvalid);
    return cartIsInvalid;
  };

  const refreshCartState = async (showIfInvalid = false) => {
    try {
      const cart = await getCart();
      if (setCartState(cart) && showIfInvalid) showModal();
    } catch (error) {
      console.error('[Panettone cart guard]', error);
    }
  };

  const shouldBlock = async (operation, body) => {
    const cart = await getCart();

    if (operation === 'add') {
      const incoming = addItemsFromBody(body).filter(item => item.quantity > 0);
      if (!incoming.length) return { block: false, cart };
      const combined = [
        ...(cart.items || []),
        ...incoming.map(item => ({ variant_id: item.id, quantity: item.quantity, properties: item.properties }))
      ];
      return { block: violatesRule(combined), cart };
    }

    const changes = changesFromBody(body);
    const hasQuantityChange = operation === 'change'
      ? changes.quantity !== null && changes.quantity !== undefined
      : Array.isArray(changes.updates) || (changes.updates && typeof changes.updates === 'object');
    if (!hasQuantityChange) return { block: false, cart };
    return { block: violatesRule(resultingItems(cart, operation, body)), cart };
  };

  const blockedResponse = cart => new Response(JSON.stringify({
    status: 422,
    kohlPanettoneBlocked: true,
    message: copy.message,
    description: copy.message,
    errors: copy.message,
    item_count: cart?.item_count || 0,
    sections: {}
  }), {
    status: 422,
    headers: { 'Content-Type': 'application/json' }
  });

  const restoreCartQuantities = cart => {
    document.querySelectorAll('[name="updates[]"][data-index]').forEach(input => {
      const item = cart?.items?.[Number.parseInt(input.dataset.index, 10) - 1];
      if (!item) return;
      input.value = item.quantity;
      input.closest('quantity-input')?.updateButtonColors?.();
    });
  };

  window.fetch = async (input, init) => {
    const operation = endpoint(input);
    if (!operation) return originalFetch(input, init);

    try {
      const body = await readBody(input, init);
      const result = await shouldBlock(operation, body);
      if (result.block) {
        restoreCartQuantities(result.cart);
        showModal();
        return blockedResponse(result.cart);
      }
    } catch (error) {
      console.error('[Panettone cart guard]', error);
    }

    const response = await originalFetch(input, init);
    if (response.ok) setTimeout(() => refreshCartState(false), 0);
    return response;
  };

  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') closeModal();
  });

  document.addEventListener('click', event => {
    if (!cartIsInvalid || !event.target.closest('[name="checkout"], a[href*="/checkout"]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    showModal();
  }, true);

  document.addEventListener('DOMContentLoaded', () => {
    createModal();
    refreshCartState(false);
  }, { once: true });
  window.KohlPanettoneCartGuard = { show: showModal, productId: PRODUCT_ID, variantId: VARIANT_ID };
})();
